import React, { FC, useEffect, useState } from 'react';

import { FormikValues, useFormik } from 'formik';
import { Box } from '@mui/system';

import {
  adminActions,
  adminUploadProfessionalDiploma,
  getPharmacistDetails,
  resetStatus,
  successSelector,
} from '@pharmaplan/common';

import { ReactComponent as UploadIcon } from '../../../../../../assets/svg/fileUploadIcon.svg';
import useDynamicProperties from '../../../../../../hooks/useDynamicProperties';
import { fileTypes } from '../../../../../common/UploadButton/UploadButton';
import { useAppDispatch } from '../../../../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../../../../hooks/useAppSelector';
import UploadDialog from '../../../../../common/UploadDialog';
import strings from '../../../../../../localization';

import { showSuccess } from '../helper';
import styles from './style';

interface IUploadDiploma {
  professionalId: string;
}

interface IDiploma {
  url: string;
  name: string;
}

const acceptedExtensions = '.pdf, .doc, .docx';

const UploadDiploma: FC<IUploadDiploma> = ({ professionalId }) => {
  const dispatch = useAppDispatch();
  const [file, setFile] = useState<IDiploma | null>(null);

  const uploadSuccess = useAppSelector((state) =>
    successSelector([adminActions.adminUploadProfessionalDiploma], state));

  const { professionalUser } = useDynamicProperties();
  const { certificateFileName, certificateUrl } = professionalUser ?? {};

  const onSubmit = (values: FormikValues) => {
    const { diploma: selectedFile } = values ?? {};

    let form = null;

    if (selectedFile) {
      form = new FormData();
      form.append('diploma', selectedFile);
    }

    dispatch(adminUploadProfessionalDiploma({ professionalId, form }));
  };

  const formik = useFormik({
    initialValues: {
      diploma: {
        name: certificateFileName,
        size: null,
      },
      isDiplomaChanged: false,
    },
    enableReinitialize: true,
    onSubmit,
  });

  const { setFieldValue, values } = formik ?? {};
  const { diploma, isDiplomaChanged } = values ?? {};
  const { size, name = '' } = diploma ?? {};

  const handleClear = () => {
    setFieldValue('diploma', null);
    setFieldValue('isDiplomaChanged', true);
  };

  const onSelect = (selectedFile: File) => {
    const url = URL.createObjectURL(selectedFile);
    setFile({ url, name: selectedFile?.name });
  };

  const openDocument = () => {
    window.open(file?.url ?? certificateUrl);
  };

  useEffect(() => {
    if (name) {
      setFieldValue('isDiplomaChanged', !!size);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size]);

  useEffect(() => {
    if (uploadSuccess) {
      showSuccess(dispatch, strings.profileDataSaved);
      dispatch(resetStatus([adminActions.adminUploadProfessionalDiploma]));
      dispatch(getPharmacistDetails(professionalId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadSuccess]);

  return (
    <UploadDialog
      fileType={fileTypes.pdf}
      formik={formik}
      message={strings.sizeOfResume}
      name="diploma"
      fileSize={2000}
      chooseLabel={strings.uploadFile}
      onSelect={onSelect}
      errorMessage={strings.sizeOfResume}
      acceptedFileTypes={acceptedExtensions}
      handleClear={handleClear}
      handleOpen={openDocument}
      disableButton={!isDiplomaChanged}
      customComponent={(
        <Box sx={styles.root}>
          <UploadIcon width={80} height={80} />
        </Box>
      )}
    />
  );
};

export default UploadDiploma;
